import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import type { FormAnalyticsBucketData } from '@/types/api';

/**
 * Submissions per period. One series, so the job the colour does is emphasis,
 * not identity: the accent carries the data and everything else stays
 * recessive. No legend — the panel heading names the series. No gridlines and
 * no y-axis; the tooltip and the two direct labels carry the values, which
 * keeps the ink on the bars.
 *
 * Fills are `var(--primary)`, which SVG resolves at paint time, so dark mode
 * follows the theme with no bridging shim. The accent was validated against
 * both the light (#fffdf8) and dark (#241e17) card surfaces.
 */
export function SubmissionChart({
  buckets,
  height = 180,
}: {
  buckets: FormAnalyticsBucketData[];
  height?: number;
}) {
  if (buckets.length === 0) {
    return null;
  }

  const max = Math.max(...buckets.map((b) => b.count));
  const lastIndex = buckets.length - 1;
  // Label only the peak and the current bucket — a number on every bar is noise.
  const labelled = new Set([buckets.findIndex((b) => b.count === max), lastIndex]);

  return (
    <div
      role="img"
      aria-label={`Submissions per ${buckets.length > 1 ? 'period' : 'period'}, ${buckets.length} periods, peaking at ${max}`}
    >
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={buckets} margin={{ top: 18, right: 4, bottom: 0, left: 4 }} barCategoryGap={2}>
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: 'var(--border)' }}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.4 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const bucket = payload[0].payload as FormAnalyticsBucketData;
              return (
                <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-2.5 py-1.5 text-xs shadow-lg">
                  <p className="font-medium">{bucket.label}</p>
                  <p className="text-[var(--color-foreground-muted)]">
                    {bucket.count} {bucket.count === 1 ? 'submission' : 'submissions'}
                  </p>
                </div>
              );
            }}
          />
          <Bar
            dataKey="count"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            label={renderSelectiveLabel(labelled)}
          >
            {buckets.map((bucket, i) => (
              <Cell
                key={bucket.starts_at}
                fill="var(--primary)"
                // The current period is still filling up, so it reads lighter
                // than the closed periods beside it rather than as a drop.
                fillOpacity={i === lastIndex ? 0.55 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Recharts hands the label renderer a loosely typed bag of geometry. Narrowing
 * it here keeps the chart body readable and the cast in one place.
 */
function renderSelectiveLabel(labelled: Set<number>) {
  return function SelectiveLabel(props: unknown) {
    const { index, value, x, y, width } = props as {
      index?: number;
      value?: number;
      x?: number;
      y?: number;
      width?: number;
    };

    if (index === undefined || !labelled.has(index) || !value) {
      return <g />;
    }

    return (
      <text
        x={(x ?? 0) + (width ?? 0) / 2}
        y={(y ?? 0) - 5}
        textAnchor="middle"
        fontSize={11}
        fill="var(--muted-foreground)"
      >
        {value}
      </text>
    );
  };
}
